import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { formatRupiah } from '@/lib/format'
import { formatRelative } from '@/lib/date'
import { SectionCard } from '@/components/dashboard/section-card'
import { EntityAvatar } from '@/components/dashboard/entity-avatar'
import { PlanBadge } from '@/components/dashboard/plan-badge'
import { InvoiceStatusBadge } from '@/components/dashboard/status-badge'
import { buttonVariants } from '@/components/ui/button'
import { INVOICES } from '@/data/invoices'

interface RecentTransactionsCardProps {
    className?: string
}

const RECENT = INVOICES.slice(0, 6)

export const RecentTransactionsCard: FC<RecentTransactionsCardProps> = ({ className }): ReactNode => {
    return (
        <SectionCard
            className={className}
            title='Transaksi terbaru'
            description='Tagihan langganan yang diterbitkan paling akhir.'
            contentClassName='px-0 pb-2'
            action={(
                <Link href='/langganan' className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                    Lihat semua
                    <ArrowRight data-icon='inline-end' />
                </Link>
            )}
        >
            <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                    <caption className='sr-only'>
                        Enam tagihan terbaru
                    </caption>

                    <thead>
                        <tr className='border-y bg-muted/40 text-xs text-muted-foreground'>
                            <th scope='col' className='px-5 py-2.5 text-left font-medium'>Pelanggan</th>
                            <th scope='col' className='hidden px-3 py-2.5 text-left font-medium md:table-cell'>Paket</th>
                            <th scope='col' className='px-3 py-2.5 text-right font-medium'>Jumlah</th>
                            <th scope='col' className='px-3 py-2.5 text-left font-medium'>Status</th>
                            <th scope='col' className='hidden px-5 py-2.5 text-right font-medium sm:table-cell'>Diterbitkan</th>
                        </tr>
                    </thead>

                    <tbody className='divide-y'>
                        {RECENT.map((invoice) => (
                            <tr key={invoice.id} className='transition-colors hover:bg-muted/40'>
                                <td className='px-5 py-3'>
                                    <div className='flex min-w-0 items-center gap-3'>
                                        <EntityAvatar name={invoice.company} size='sm' />
                                        <div className='min-w-0'>
                                            <p className='max-w-[220px] truncate font-medium text-foreground'>
                                                {invoice.company}
                                            </p>
                                            <p className='font-mono text-[11px] text-muted-foreground'>
                                                {invoice.number}
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td className='hidden px-3 py-3 md:table-cell'>
                                    <PlanBadge plan={invoice.plan} />
                                </td>
                                <td className='px-3 py-3 text-right font-medium whitespace-nowrap text-foreground tabular-nums'>
                                    {formatRupiah(invoice.amount)}
                                </td>
                                <td className='px-3 py-3'>
                                    <InvoiceStatusBadge status={invoice.status} />
                                </td>
                                <td className='hidden px-5 py-3 text-right whitespace-nowrap text-muted-foreground sm:table-cell'>
                                    {formatRelative(invoice.issuedAt)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </SectionCard>
    )
}
